/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

import { ConfluenceWorkspaceAri } from '@atlassian/ari/confluence';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import { parsePagePeople } from '../utils/parsePagePeople.ts';
import {
	getPagePeopleRelationships,
	type PagePeopleRelationshipDefinition,
} from '../utils/pagePeopleRelationships.ts';
import { MAX_LISTED_PAGE_PEOPLE } from '../utils/pagePeople.ts';
import type { PageAgentContributor } from '../utils/normalizePageAgentContributors.ts';
import {
	rankPagePeopleAndAgents,
	type RankedPagePersonOrAgent,
} from '../utils/rankPagePeopleAndAgents.ts';

import type { PageAgentViewer } from './usePageAgentViewers.ts';
import type {
	usePagePeopleQuery$data,
	usePagePeopleQuery$variables,
} from './__generated__/usePagePeopleQuery.graphql.ts';
import { getPagePeopleFailedEvent, getPagePeopleSucceededEvent } from './pagePeopleAnalytics.ts';

const CANDIDATES_PER_RELATIONSHIP = 10;

const classifyError = (error: ApolloErrorInterop): 'network' | 'graphql' =>
	!error.networkError && error.graphQLErrors?.length ? 'graphql' : 'network';

const buildRelationshipBranch = (
	pageAri: string,
	{ cypherName, relationship, direction }: PagePeopleRelationshipDefinition,
): string => {
	const match =
		direction === 'incoming'
			? `MATCH (person)-[rel:${cypherName}]->(page {ari: "${pageAri}"})`
			: `MATCH (page {ari: "${pageAri}"})-[rel:${cypherName}]->(person)`;

	return [
		match,
		'WITH person, rel',
		'ORDER BY rel.lastUpdated DESC',
		`LIMIT ${CANDIDATES_PER_RELATIONSHIP}`,
		`RETURN person AS person, "${relationship}" AS relationship, "${direction}" AS direction, rel.lastUpdated AS relationshipTimestamp`,
	].join('\n');
};

export const buildPagePeopleCypher = (pageAri: string, contentType?: BylineContentType): string =>
	getPagePeopleRelationships(contentType)
		.map((definition) => buildRelationshipBranch(pageAri, definition))
		.join('\nUNION ALL\n');

const PagePeopleQuery = graphql`
	query usePagePeopleQuery(
		$cypherQuery: String!
		$queryContext: String!
		$version: GraphStoreCypherQueryV2VersionEnum
	) {
		graphStore_cypherQueryV2(
			query: $cypherQuery
			queryContext: $queryContext
			version: $version
			first: 110
		) @optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							... on GraphStoreCypherQueryV2AriNode {
								id
								data {
									__typename
									... on AtlassianAccountUser {
										id
										accountId
										name
										picture
										accountStatus
										extendedProfile {
											jobTitle
										}
									}
									... on CustomerUser {
										id
										accountId
										name
										picture
										accountStatus
									}
								}
							}
							... on GraphStoreCypherQueryV2StringObject {
								stringValue: value
							}
							... on GraphStoreCypherQueryV2TimestampObject {
								timestampValue: value
							}
						}
					}
				}
			}
		}
	}
`;

export type UsePagePeopleOptions = {
	/** Holds discovery while the Teamwork Graph modal is closed. Defaults to true. */
	enabled?: boolean;
	/** Selects the ARI the content is stored under in the graph. Undefined means page. */
	contentType?: BylineContentType;
	agents?: readonly PageAgentViewer[];
	agentContributors?: readonly PageAgentContributor[];
};

export type UsePagePeopleResult = {
	/** All ranked candidates, including partial people data, for canvas selection before any cap. */
	rankedPeopleAndAgents: RankedPagePersonOrAgent[];
	/** Available section rows, capped independently of the canvas. */
	peopleAndAgents: RankedPagePersonOrAgent[];
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

/**
 * Discovers real people directly connected to a Confluence page through the current Teamwork Graph
 * registry snapshot, then ranks them with agents by role and recency for both surfaces.
 * This client-only request must remain gated by the owning surface's open state.
 */
export const usePagePeople = (
	contentId: string | undefined,
	options?: UsePagePeopleOptions,
): UsePagePeopleResult => {
	const { cloudId, activationId } = useSessionData();
	// `@confluence/analytics`, not the platform analytics-bridge helper. `fireOperationalAnalytics`
	// fires on the `analyticsBridge` channel, and `AnalyticsListener` only invokes its handler when
	// its own `channel` is `'*'` or equal to the event's; `ConfluenceAnalyticsListener` passes no
	// `channel`, so bridge events never reached it at all. See `.agents/teamwork-graph-modal.md`.
	// CCINT-9105
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasFiredRef = useRef(false);
	const skip =
		options?.enabled === false ||
		!contentId ||
		!cloudId ||
		!activationId ||
		!isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment');

	const reportError = useCallback(
		(error: ApolloErrorInterop) => {
			getMonitoringClient().submitError(error, { attribution: Attribution.INTEGRATIONS });
			markErrorAsHandled(error);
			createEvent(getPagePeopleFailedEvent({ errorType: classifyError(error) })).fire();
		},
		[createEvent],
	);

	const { data, loading, error } = useQueryInterop<
		usePagePeopleQuery$data,
		usePagePeopleQuery$variables
	>(PagePeopleQuery, {
		variables: {
			cypherQuery: skip
				? ''
				: buildPagePeopleCypher(
						buildContentAri(cloudId, contentId, options?.contentType),
						options?.contentType,
					),
			queryContext: skip
				? ''
				: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			version: 'V3',
		},
		skip,
		interopApolloAggClient: true,
		errorPolicy: 'all',
		onError: reportError,
	});

	const candidates = useMemo(() => (skip ? [] : parsePagePeople(data)), [data, skip]);
	const rankedPeopleAndAgents = useMemo(
		() =>
			rankPagePeopleAndAgents(candidates, options?.agents ?? [], options?.agentContributors ?? []),
		[candidates, options?.agents, options?.agentContributors],
	);
	const peopleAndAgents = useMemo(
		() =>
			rankedPeopleAndAgents
				.filter(({ agent }) => agent || (!loading && !error))
				.slice(0, MAX_LISTED_PAGE_PEOPLE),
		[rankedPeopleAndAgents, loading, error],
	);

	// Once per hook instance, so a re-render or a cache update does not inflate the
	// success count. Preserve the discovered-human count (capped at ten), including relationships
	// excluded from display. Neither agent availability nor canvas selection changes this metric.
	const discoveredPeopleCount = Math.min(candidates.length, MAX_LISTED_PAGE_PEOPLE);
	useEffect(() => {
		if (skip || loading || error || hasFiredRef.current) {
			return;
		}

		hasFiredRef.current = true;
		createEvent(getPagePeopleSucceededEvent({ count: discoveredPeopleCount })).fire();
	}, [skip, loading, error, discoveredPeopleCount, createEvent]);

	return { rankedPeopleAndAgents, peopleAndAgents, loading: !skip && loading, error };
};
